import { useFlag } from '@unleash/proxy-client-react';
import { Tooltip } from 'antd';
import { debounce } from 'lodash';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import InfiniteViewer from 'react-infinite-viewer';
import Moveable from 'react-moveable';
import Selecto from 'react-selecto';
import styled, { css } from 'styled-components';

import { CopySvg, DeleteSvg, PlusSvg } from '../../assets/icons';
import PadlockSvg from '../../assets/icons/PadlockSvg';
import theme from '../../assets/theme';
import { useDesignerDataContext } from '../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../hooks/useDesignerUIState';
import useMoveableCallbacks from '../../hooks/useMoveableCallbacks';
import { LessonAttachment } from '../../services/backendService/backendService';
import { isTouchDevice } from '../../utils/client';
import { zIndexes } from '../../utils/zIndexes';
import { IconWrapper } from '../_atoms/IconWrapper';
import ActivityIndicator from './ActivityIndicator';
import EditorSlideWrapperViewer from './EditorSlideWrapperViewer';
import { RichTextEditorType } from './SlateRichTextEditor';
import { OnElementsChangeProps, Slide, SlideElement } from './slide.types';
import { AiConnectionAction } from './SlideEditorToolbar';
import SlideElementIframe from './SlideElementIframe';
import SlideElementTextEditor from './SlideElementTextEditor';
import SlideElementTextViewer from './SlideElementTextViewer';
import {
  getHighestXFromSlideElements,
  getLowestXFromSlideElements,
  getLowestYFromSlideElements,
  isSlideElementGeneratorImage,
  isSlideElementGeneratorText,
  isSlideElementIframe,
  isSlideElementShape,
  isSlideElementText,
  MAX_ZOOM,
  MIN_ZOOM,
  PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME,
  RICHTEXTEDITOR_ID_PREFIX,
} from './slideUtil';

const ProxySlideEditorSurface = ({
  elementsToRender,
  aspect,
  setSelectedElements,
  selectedElements,
  onElementsDelete,
  onElementsChange,
  slide,
  gameId,
  containerRef,
  editingTextElementId,
  onElementsDuplicate,
  infiniteViewerRef,
  zoom,
  lessonAttachments,
}: {
  elementsToRender: SlideElement[];
  aspect: { width: number; height: number };
  setSelectedElements: (selectedElements: SlideElement[]) => void;
  selectedElements: SlideElement[] | null;
  infiniteViewerRef: React.RefObject<InfiniteViewer>;
  onElementsDelete: (elements: SlideElement[]) => void;
  onElementsChange: (props: OnElementsChangeProps) => void;
  gameId?: string;
  slide: Slide;
  editingTextElementId?: string;
  containerRef: RefObject<HTMLDivElement>;
  onElementsDuplicate: (metricsEvent?: string) => void;
  zoom: number;
  lessonAttachments?: LessonAttachment[];
}) => {
  const [isHoldingShift, setIsHoldingShift] = useState(false);
  const [isHoldingSpace, setIsHoldingSpace] = useState(false);
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const selectedTargetElements = useDesignerUIContext(
    context => context.state.selectedTargetElements,
  );
  const anElementIsBeingTransformed = useDesignerUIContext(
    context => context.state.anElementIsBeingTransformed,
  );
  const { tab } = useDesignerUIContext(c => c.state.selectedTab);
  const richTextEditor = useDesignerUIContext(context => context.state.richTextEditor);
  const snappableContainerRef = useRef<HTMLDivElement>(null);
  const proxySlideContainerRef = useRef<HTMLDivElement>(null);

  const handleElementsDelete = useCallback(
    (elements: SlideElement[]) => {
      // Reset editing and transform state when deleting an element
      onElementsDelete(elements);
      dispatch({ type: DesignerUIStateActions.deselectElements });
    },
    [dispatch, onElementsDelete],
  );
  const handleElementInteraction = useCallback(
    (element: SlideElement) => {
      if (isSlideElementText(element)) {
        dispatch({
          type: DesignerUIStateActions.startEditingTextElement,
          elementId: element.id,
        });
      }
      if (isSlideElementIframe(element)) {
        dispatch({
          type: DesignerUIStateActions.allowInteractionWithIframe,
        });
      }
    },
    [dispatch],
  );

  const handleElementSelect = useCallback(
    (newTargets: Array<HTMLElement | SVGElement>) => {
      if (newTargets.length === 0) {
        dispatch({
          type: DesignerUIStateActions.deselectElements,
        });
        setSelectedElements([]);
        return;
      }

      const newSelectedElements = elementsToRender.filter(e =>
        newTargets.some(s => s.id === e.id),
      );
      setSelectedElements(newSelectedElements);
    },
    [dispatch, elementsToRender, setSelectedElements],
  );

  const {
    isSelecting,
    selectoRef,
    moveableRef,
    resizeDirection,
    onMoveableDragStart,
    onMoveableDrag,
    onMoveableDragEnd,
    onMoveableDragGroupStart,
    onMoveableDragGroup,
    onMoveableDragGroupEnd,
    onMoveableResizeStart,
    onMoveableResize,
    onMoveableResizeEnd,
    onMoveableResizeGroupStart,
    onMoveableResizeGroup,
    onMoveableResizeGroupEnd,
    onMoveableRotate,
    onMoveableRotateEnd,
    onMoveableRotateGroup,
    onMoveableRotateGroupEnd,
    onSelectStart,
    onSelect,
    onSelectEnd,
    onSelectDragStart,
  } = useMoveableCallbacks({
    elementsToRender,
    aspect,
    onElementsChange,
    handleElementsDelete,
    handleElementInteraction,
    selectedElements,
    handleElementSelect,
  });
  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');

  const viewport = useRef<HTMLDivElement>(null);
  const isTextElementSelected = useMemo(() => {
    if (!selectedElements) return false;
    if (selectedElements.length !== 1) return false;
    return isSlideElementText(selectedElements[0]);
  }, [selectedElements]);

  const isGeneratorElementSelected = useMemo(() => {
    if (!selectedElements) return false;
    if (selectedElements.length !== 1) return false;
    if (isSlideElementGeneratorText(selectedElements[0])) return true;
    if (isSlideElementGeneratorImage(selectedElements[0])) return true;
    return false;
  }, [selectedElements]);
  const elementsToSnap = useMemo(() => {
    const snapTargets = elementsToRender.map(element => {
      if (selectedElements?.some(e => e.id === element.id)) return;
      return { element: `.target${element.id}`, className: 'dashed' };
    });
    return snapTargets.filter(e => e !== undefined);
  }, [elementsToRender, selectedElements]);

  // sets the helplines for the moveable box
  const helplines = useMemo(() => {
    const horizontalHelplines: { pos: number; className: string }[] = [
      { pos: 0, className: 'solidHorizontal' },
      { pos: 1080, className: 'solidHorizontal' },
      { pos: 1080 / 2, className: 'solidHorizontal' },
    ];
    const verticaltHelplines: { pos: number; className: string }[] = [
      { pos: 0, className: 'solidVertical' },
      { pos: 1920, className: 'solidVertical' },
      { pos: 1920 / 2, className: 'solidVertical' },
    ];

    return {
      horizontal: horizontalHelplines,
      vertical: verticaltHelplines,
    };
  }, []);
  // Locks the aspect element of certain elements
  const aspectShouldBeLocked = useMemo(() => {
    if (selectedElements?.length !== 1) return true;
    const selectedElement = selectedElements[0];
    if (selectedElement.type === 'svg') return true;
    if (selectedElement.type === 'image') return true;
    if (selectedElement.type === 'attachmentPassage') return true;
    if (isHoldingShift) return true;

    if (isSlideElementText(selectedElement) && !!resizeDirection) {
      if (Math.abs(resizeDirection[0]) > 0 && Math.abs(resizeDirection[1]) > 0)
        return true;
      return false;
    }
  }, [isHoldingShift, resizeDirection, selectedElements]);
  const handleUnlockElements = useCallback(
    (elements: SlideElement[]) => {
      const updatedElements = elements
        .filter(e => !e.lockedFromDeletion)
        .map(e => {
          return { ...e, lockedPosition: false };
        });
      onElementsChange({ updatedElements });
    },
    [onElementsChange],
  );
  const renderDirections = useMemo(() => {
    if (!selectedElements) return ['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'];
    if (selectedElements.length === 1) {
      const selectedElement = selectedElements[0];

      if (isSlideElementText(selectedElement)) {
        return ['nw', 'ne', 'w', 'e', 'sw', 'se'];
      }
      if (isSlideElementShape(selectedElement)) {
        return ['nw', 'ne', 'w', 'e', 'n', 's', 'sw', 'se'];
      }

      if (isSlideElementIframe(selectedElement)) {
        return ['nw', 'ne', 'w', 'e', 'n', 's', 'sw', 'se'];
      }
    }
    return ['nw', 'ne', 'sw', 'se'];
  }, [selectedElements]);
  const isPositionLocked = useMemo(() => {
    if (!selectedElements) return true;
    if (selectedElements.length === 0) return true;
    if (selectedElements?.some(e => e.lockedPosition)) return true;
    return false;
  }, [selectedElements]);

  const selectedElementsTopMiddlePosition = useMemo(() => {
    if (!selectedElements) return;
    if (selectedElements.length === 0) return;

    const firstElementLowestY = getLowestYFromSlideElements(selectedElements);
    if (!firstElementLowestY) return;
    const firstElementLowestX = getLowestXFromSlideElements(selectedElements);
    if (!firstElementLowestX) return;
    const firstElementHighestX = getHighestXFromSlideElements(selectedElements);
    if (!firstElementHighestX) return;

    const topEdgeWidth = firstElementHighestX.x - firstElementLowestX.x;

    return {
      x: firstElementLowestX.x + topEdgeWidth / 2,
      y: firstElementLowestY.y,
    };
  }, [selectedElements]);

  const showLockedPadlock = useMemo(() => {
    if (!selectedElements) return;

    if (selectedElements.length === 0) return;

    if (selectedElements?.some(e => e.lockedPosition)) {
      // finds top position of the selectedElements position

      const isDisabled = selectedElements.some(
        e => e.lockedFromDeletion || e.isPollOption,
      );
      let tooltip = 'Click to unlock position';
      let show = true;
      if (isDisabled) {
        tooltip = 'Can not unlock postions. Contain fixed elements';
      }
      if (selectedElements.every(e => e.isPollOption)) {
        show = false;
      }
      return {
        show: show,
        disabled: isDisabled,
        tooltip: tooltip,
      };
    }

    return;
  }, [selectedElements]);

  const showDeleteAndDuplicateTools = useMemo(() => {
    if (!selectedElements) return;

    if (selectedElements.length === 0) return;

    if (selectedElements.every(e => e.isPollOption)) {
      //poll options should be able to be deleted
      return {
        show: true,
        isPollOptions: true,
        disabled: false,
        metricsEvent: 'duplicate.pollOption',
      };
    }

    if (selectedElements?.some(e => !e.lockedPosition)) {
      // finds top position of the selectedElements position

      const isDisabled = selectedElements.some(e => e.lockedFromDeletion);

      const show = anElementIsBeingTransformed ? false : true;

      return {
        show: show,
        disabled: isDisabled,
        metricsEvent: 'duplicate.element',
      };
    }

    return;
  }, [selectedElements, anElementIsBeingTransformed]);
  const handleActivityIndicatorClicked = useCallback(() => {
    dispatch({
      type: DesignerUIStateActions.setSelectedTab,
      payload: {
        tab: 'interactiveElements',
      },
    });
  }, [dispatch]);
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      const infiniteViewer = infiniteViewerRef?.current;
      if (infiniteViewer) {
        const zoom = infiniteViewer.getZoom();
        const scrollWidth = infiniteViewer.getScrollWidth();
        const scrollHeight = infiniteViewer.getScrollHeight();
        const scrollWidthAdjusted = (scrollWidth / zoom) * 0.8;
        const scrollHeightAdjusted = (scrollHeight / zoom) * 0.8;

        const scrollLeft = infiniteViewer.getScrollLeft();
        const scrollTop = infiniteViewer.getScrollTop();

        if (scrollTop > 1080 * 0.8 && e.deltaY > 0) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (scrollTop < -scrollHeightAdjusted && e.deltaY < 0) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (scrollLeft > 1920 * 0.8 && e.deltaX > 0) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (scrollLeft < -scrollWidthAdjusted && e.deltaX < 0) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    },
    [infiniteViewerRef],
  );
  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      const infiniteViewer = infiniteViewerRef?.current;

      dispatch({
        type: DesignerUIStateActions.setZoom,
        zoomAction: 'fit',
        containerFitHeight: height,
        containerFitWidth: width,
        infiniteViewer: !!infiniteViewer ? infiniteViewer : undefined,
      });

      proxySlideContainerRef.current?.addEventListener('wheel', handleWheel, {
        passive: false,
      });

      return () => {
        proxySlideContainerRef.current?.removeEventListener('wheel', handleWheel);
      };
    }
  }, [infiniteViewerRef, dispatch]);
  return (
    <ProxyEditingAreaContainer>
      <Hotkeys
        keyName="Esc"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          if (selectedElements && !editingTextElementId) {
            dispatch({ type: DesignerUIStateActions.deselectElements });
          }
        }}
      />
      <Hotkeys
        keyName="command+a,ctrl+a"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          const allSelectedElements = elementsToRender.filter(
            e => !e.isPollOption && !e.lockedFromDeletion && !e.lockedPosition,
          );
          const selectoElements = selectoRef.current?.getSelectableElements();
          if (selectoElements) {
            const newSelectedElements = allSelectedElements.filter(e =>
              selectoElements.some(s => s.id === e.id),
            );
            setSelectedElements(newSelectedElements);
          }
        }}
      />
      <Hotkeys
        keyName="Space"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          setIsHoldingSpace(true);
        }}
        onKeyUp={(_, evnt) => {
          evnt.preventDefault();
          setIsHoldingSpace(false);
        }}
        allowRepeat={true}
      />

      <ProxySlideContainer
        $isHoldingSpace={isHoldingSpace}
        $isTouchDevice={isTouchDevice()}
        ref={proxySlideContainerRef}
      >
        <InfiniteViewer
          ref={infiniteViewerRef}
          className={'viewer'}
          usePinch={true}
          zoom={zoom}
          zoomX={zoom}
          useOverflowScroll={true}
          zoomY={zoom}
          useWheelScroll={true}
          useAutoZoom={true}
          threshold={1}
          useGesture={true}
          useMouseDrag={isHoldingSpace}
          useWheelPinch={true}
          wheelScale={0.005}
          wheelPinchKey={'meta'}
          pinchThreshold={0}
          onPinch={({ zoom, inputEvent }) => {
            if (inputEvent.type === 'touchmove') {
              inputEvent.preventDefault();
              return;
            }
            if (zoom < MIN_ZOOM || zoom > MAX_ZOOM) return;
            dispatch({ zoom: zoom, type: DesignerUIStateActions.setZoom });
          }}
          zoomRange={[MIN_ZOOM, MAX_ZOOM]}
        >
          <div className="viewport" ref={viewport}>
            <div style={{ width: '1920px', height: '1080px', position: 'absolute' }}>
              <EditorSlideWrapperViewer
                aspect={aspect}
                slide={slide}
                elementsToRender={elementsToRender}
                selectedElements={selectedElements}
                backgroundMedia={slide.backgroundMedia}
                gameId={gameId}
                lessonAttachments={lessonAttachments}
              />
            </div>
            <StyledMoveable
              ref={moveableRef}
              $zoom={zoom}
              target={selectedTargetElements}
              rotatable={!isPositionLocked && !isSelecting}
              resizable={!isPositionLocked && !isSelecting}
              snappable={true}
              useMutationObserver={true}
              useResizeObserver={true}
              $isEditingText={!!editingTextElementId}
              $isTextElement={isTextElementSelected}
              snapContainer={snappableContainerRef.current}
              elementGuidelines={elementsToSnap}
              verticalGuidelines={helplines?.vertical}
              horizontalGuidelines={helplines?.horizontal}
              startDragRotate={0}
              isDisplayGridGuidelines={true}
              throttleDragRotate={0}
              zoom={1 / zoom}
              isDisplaySnapDigit={false}
              scalable={false}
              keepRatio={aspectShouldBeLocked}
              draggable={true} // keeps it true so we can interact with locked positions
              $multipleElementsSelected={
                !!selectedElements && selectedElements?.length > 1
              }
              throttleDrag={0}
              edgeDraggable={false}
              renderDirections={renderDirections}
              snapThreshold={2 / zoom}
              rotationPosition={'bottom'}
              snapDirections={{
                top: true,
                bottom: true,
                left: true,
                right: true,
                center: true,
                middle: true,
              }}
              snapRotationDegrees={[0, 45, 90, 135, 180, 225, 270, 315]}
              $isGeneratorElement={isGeneratorElementSelected}
              onClickGroup={e => {
                selectoRef.current!.clickTarget(e.inputEvent, e.inputTarget);
              }}
              onClick={e => {
                const target = e.target;
                const selectedElement = elementsToRender.find(e => e.id === target.id);
                if (!selectedElement) return;

                if (e.isTrusted) {
                  handleElementInteraction(selectedElement);
                  return;
                }
                if (e.isDouble) {
                  handleElementInteraction(selectedElement);
                  return;
                }
              }}
              origin={false}
              onDragStart={onMoveableDragStart}
              onDrag={onMoveableDrag}
              onDragEnd={onMoveableDragEnd}
              onDragGroupStart={onMoveableDragGroupStart}
              onDragGroup={onMoveableDragGroup}
              onDragGroupEnd={onMoveableDragGroupEnd}
              onRender={({ target, cssText }) => {
                // allows for the drag box for be rerendered when dragging
                target.style.cssText += cssText;
              }}
              onRenderGroup={e => {
                e.events.forEach(ev => {
                  ev.target.style.cssText += ev.cssText;
                });
              }}
              onResizeStart={onMoveableResizeStart}
              onResize={onMoveableResize}
              onResizeEnd={onMoveableResizeEnd}
              onResizeGroupStart={onMoveableResizeGroupStart}
              onResizeGroup={onMoveableResizeGroup}
              onResizeGroupEnd={onMoveableResizeGroupEnd}
              onRotate={onMoveableRotate}
              onRotateEnd={onMoveableRotateEnd}
              onRotateGroup={onMoveableRotateGroup}
              onRotateGroupEnd={onMoveableRotateGroupEnd}
            />
            <div className="elements selecto-area">
              {elementsToRender.map((element, i) => {
                return (
                  <ProxySlideElement
                    key={element.id}
                    isHoldingSpace={isHoldingSpace}
                    selectedSlide={slide}
                    isSelected={selectedElements?.some(e => e.id === element.id) ?? false}
                    element={element}
                    handleElementSelect={handleElementSelect}
                    onElementsChange={onElementsChange}
                    index={i}
                    richTextEditor={richTextEditor}
                    editingTextElementId={editingTextElementId}
                    moveableRef={moveableRef}
                    selectoRef={selectoRef}
                    anElementIsBeingTransformed={anElementIsBeingTransformed}
                  />
                );
              })}
            </div>
            {showLockedPadlock &&
              showLockedPadlock.show &&
              selectedElements &&
              selectedElementsTopMiddlePosition && (
                <Tooltip title={showLockedPadlock.tooltip} mouseEnterDelay={1}>
                  <LockedPositionOverlay
                    className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
                    $disabled={showLockedPadlock.disabled}
                    $x={selectedElementsTopMiddlePosition.x}
                    $y={selectedElementsTopMiddlePosition.y}
                    $zoom={zoom}
                    onClick={() => {
                      if (showLockedPadlock.disabled) return;
                      handleUnlockElements(selectedElements);
                    }}
                    onTouchEnd={() => {
                      if (showLockedPadlock.disabled) return;
                      handleUnlockElements(selectedElements);
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <IconOverLay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
                      <IconWrapper>
                        <PadlockSvg />
                      </IconWrapper>
                    </div>
                  </LockedPositionOverlay>
                </Tooltip>
              )}

            {showDeleteAndDuplicateTools &&
              selectedElementsTopMiddlePosition &&
              showDeleteAndDuplicateTools.show &&
              (!showLockedPadlock || !showLockedPadlock.show) &&
              selectedElements && (
                <DuplicateAndDeleteOverlay
                  className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME}
                  $disabled={showDeleteAndDuplicateTools.disabled}
                  $x={selectedElementsTopMiddlePosition.x}
                  $y={selectedElementsTopMiddlePosition.y}
                  $zoom={zoom}
                  $includesWandTool={
                    isAITemplatesEnabled &&
                    (!!selectedElements[0].promptSourceId || tab === 'prompts')
                  }
                >
                  <Tooltip
                    title={
                      showDeleteAndDuplicateTools.isPollOptions ? 'Add new' : 'Duplicate'
                    }
                    mouseEnterDelay={1}
                  >
                    <div
                      style={{ position: 'relative' }}
                      onClick={() => {
                        if (showDeleteAndDuplicateTools.disabled) return;
                        onElementsDuplicate(showDeleteAndDuplicateTools.metricsEvent);
                      }}
                      onTouchEnd={() => {
                        if (showDeleteAndDuplicateTools.disabled) return;
                        onElementsDuplicate(showDeleteAndDuplicateTools.metricsEvent);
                      }}
                    >
                      <IconOverLay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
                      <IconWrapper>
                        {showDeleteAndDuplicateTools.isPollOptions ? (
                          <PlusSvg />
                        ) : (
                          <CopySvg />
                        )}
                      </IconWrapper>
                    </div>
                  </Tooltip>
                  {isAITemplatesEnabled &&
                    (!!selectedElements[0].promptSourceId || tab === 'prompts') && (
                      <AiConnectionAction
                        onElementsChange={onElementsChange}
                        selectedElement={selectedElements[0]}
                        isMiniToolbar={true}
                      />
                    )}
                  <Tooltip title={'Delete'} mouseEnterDelay={1}>
                    <div
                      style={{ position: 'relative' }}
                      onClick={() => {
                        if (showDeleteAndDuplicateTools.disabled) return;
                        onElementsDelete(selectedElements);
                      }}
                      onTouchEnd={() => {
                        if (showDeleteAndDuplicateTools.disabled) return;
                        onElementsDelete(selectedElements);
                      }}
                    >
                      <IconOverLay className={PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME} />
                      <IconWrapper>
                        <DeleteSvg />
                      </IconWrapper>
                    </div>
                  </Tooltip>
                </DuplicateAndDeleteOverlay>
              )}
            {slide.type !== 'slide' && (
              <ActivityIndicator onClick={handleActivityIndicatorClicked} slide={slide} />
            )}
          </div>
        </InfiniteViewer>
        <Selecto
          ref={selectoRef}
          dragContainer={containerRef.current}
          selectableTargets={isHoldingSpace ? [] : ['.target']}
          hitRate={0}
          selectByClick={!isHoldingSpace}
          toggleContinueSelect={['shift']}
          ratio={0}
          onKeydown={() => {
            // since shift is the key for Selecto we use that keydown to set the state
            setIsHoldingShift(true);
          }}
          onKeyup={() => {
            setIsHoldingShift(false);
          }}
          onSelectStart={onSelectStart}
          onSelect={onSelect}
          onSelectEnd={onSelectEnd}
          preventClickEventOnDragStart={false}
          selectFromInside={false}
          onDragStart={onSelectDragStart}
        />
      </ProxySlideContainer>
    </ProxyEditingAreaContainer>
  );
};

const ProxyEditingAreaContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const ProxySlideContainer = styled.div<{
  $isHoldingSpace: boolean;
  $isTouchDevice: boolean;
}>`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  .viewer {
    width: 100%;
    height: 100%;
  }
  .selecto-selection {
    border: ${({ $isHoldingSpace }) =>
      $isHoldingSpace ? 'none' : ` 0.5px solid ${theme.colors.orange} `};
    border-radius: 5px;
    background-color: ${({ $isHoldingSpace }) =>
      $isHoldingSpace ? 'transparent' : theme.colors.translucentOrange} !important;
  }
  .infinite-viewer-scroll-thumb {
    background: #c7c7c7 !important;
    ${props => props.$isTouchDevice && `opacity: 1 !important`}
  }
  .infinite-viewer-scroll-bar.infinite-viewer-vertical-scroll-bar {
    .infinite-viewer-scroll-thumb {
      width: 10px !important;
    }
  }
  .infinite-viewer-scroll-bar.infinite-viewer-horizontal-scroll-bar {
    .infinite-viewer-scroll-thumb {
      height: 10px !important;
    }
  }
  .infinite-viewer-wrapper {
    ${props =>
      props.$isHoldingSpace &&
      css`
        cursor: grab !important;
      `}
  }
`;

const ProxySlideElement = ({
  element,
  index,
  isSelected,
  richTextEditor,
  handleElementSelect,
  editingTextElementId,
  moveableRef,
  selectoRef,
  selectedSlide,
  anElementIsBeingTransformed,
  isHoldingSpace,
}: {
  element: SlideElement;
  selectedSlide: Slide;
  index: number;
  isHoldingSpace: boolean;
  isSelected: boolean;
  handleElementSelect: (selectedElements: Array<HTMLElement | SVGElement>) => void;
  onElementsChange: (props: OnElementsChangeProps) => void;
  richTextEditor: RichTextEditorType | null;
  editingTextElementId: string | undefined;
  moveableRef: RefObject<Moveable>;
  selectoRef: RefObject<Selecto>;
  anElementIsBeingTransformed: boolean;
}) => {
  const textElementHeightRef = useRef<HTMLDivElement>(null);
  const [localElement, setLocalElement] = useState<SlideElement>(element);
  const observerTextvalueRef = useRef<string>(localElement.textValue || '');
  const observerWidthRef = useRef<number>(localElement.width || 30);
  const observerHeightRef = useRef<number>(localElement.height || 30);

  const onElementsChange = useDesignerDataContext(
    context => context.handleElementsChange,
  );

  const canInteractWithIframe = useDesignerUIContext(
    context => context.state.canInteractWithIframe,
  );

  const elementTargetRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnElementSave = useCallback(
    debounce((localElement: SlideElement, element: SlideElement) => {
      if (JSON.stringify(localElement) !== JSON.stringify(element)) {
        onElementsChange({ updatedElements: [localElement], index });
      }
    }, 200), // the small debounce here is to optimize rendering performance
    [onElementsChange, index],
  );

  const onTextElementChange = useCallback(
    (newText: string, localElement: SlideElement, containerHeight: number) => {
      const newHeight = containerHeight > 0 ? containerHeight : localElement.height;
      const newElement: SlideElement = { ...localElement, height: newHeight };
      if (newText === localElement.textValue) return;

      newElement.textValue = newText;
      setLocalElement(newElement);

      // When we are changing text from the toolbar - skip the debounce
      if (isSelected && !editingTextElementId) {
        onElementsChange({ updatedElements: [newElement], index });
      } else {
        debouncedOnElementSave(newElement, element);
      }
    },
    [
      debouncedOnElementSave,
      element,
      isSelected,
      editingTextElementId,
      onElementsChange,
      index,
    ],
  );
  const setSelectedElements = useCallback(
    (element: SlideElement | null) => {
      const targetElementId = elementTargetRef.current?.id;
      if (!element) {
        handleElementSelect([]);
        return;
      }
      if (element.id !== targetElementId) {
        handleElementSelect([]);
        return;
      }
      const target = elementTargetRef.current;
      if (target) {
        handleElementSelect([target]);
      }
      return;
    },
    [handleElementSelect],
  );

  // this function updates the height of the text element when hovering elements
  //  this is often due to generated text is not the same height as the text generated
  const handleMouseEnter = useCallback(
    (element: SlideElement) => {
      if (isSlideElementText(element)) {
        const targets = selectoRef.current!.getSelectableElements();

        const target = targets.find(t => t.id === element.id);
        if (target) {
          const childDiv = target.childNodes?.[0] as HTMLDivElement;

          if (!childDiv) return;
          const innerTextDiv = childDiv.childNodes?.[0] as HTMLDivElement;
          if (!innerTextDiv) return;
          const innerTextHeight = innerTextDiv.clientHeight * (element.scale || 1);
          if (Math.abs(innerTextHeight - element.height) > 10) {
            target.style.height = `${innerTextHeight}px`;
            onElementsChange({
              updatedElements: [
                {
                  ...element,
                  height: innerTextHeight,
                },
              ],
            });
          }
          return;
        }
      }
    },
    [onElementsChange, selectoRef],
  );

  useEffect(() => {
    if (element) {
      setLocalElement(element);
    }
  }, [element, setLocalElement]);
  useEffect(() => {
    /*
    In order to get the right height on Text elements, we need to use ResizeObserver. This is due to the text field
    can have spaces and enters which makes the height of the element dependent on the content
    This useEffect is used to set the height of the moveable rectangle not mutation of the data
    */
    if (!isSlideElementText(localElement)) return;
    if (!textElementHeightRef.current) {
      return;
    }
    const ro = new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        if (
          localElement.textValue !== observerTextvalueRef.current ||
          (localElement.height !== observerHeightRef.current &&
            !localElement.isPollOption) ||
          localElement.width !== observerWidthRef.current
        ) {
          const { height } = entries[0].contentRect;
          if (height) {
            const targets = moveableRef.current?.getTargets();
            const elementScale = localElement.scale || 1;
            if (targets) {
              const target = targets.find(t => t.id === localElement.id);

              if (target) {
                target.style.height = `${height * elementScale}px`;
                onElementsChange({
                  updatedElements: [
                    {
                      ...localElement,
                      height: height * elementScale,
                    },
                  ],
                  slide: selectedSlide,
                });
                observerTextvalueRef.current = localElement.textValue;
                observerHeightRef.current = height * elementScale;
                observerWidthRef.current = localElement.width;
              }
            }
          }
        }
      });
    });

    ro.observe(textElementHeightRef.current as HTMLElement);

    return () => {
      if (ro) ro.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localElement, onElementsChange, selectedSlide]);
  return (
    <ElementContainer
      $isSelected={isSelected}
      onMouseEnter={() => handleMouseEnter(element)}
      $isGeneratorElement={
        isSlideElementGeneratorText(localElement) ||
        isSlideElementGeneratorImage(localElement)
      }
      $isTextElement={isSlideElementText(localElement)}
      $anElementIsTransforming={anElementIsBeingTransformed}
      id={element.id}
      ref={elementTargetRef}
      onClick={() => setSelectedElements(localElement)}
      style={{
        height: localElement.height,
        width: localElement.width,
        transform: `translate3d(
          ${localElement.x}px,
          ${localElement.y}px,
          0
        )
        rotate(${localElement.angle}deg)`,
      }}
      className={`target target${localElement.id} ${
        element.lockedPosition ? 'locked' : ''
      }`}
    >
      {/* This view is only used for hovering text elements to make sure the hovering box is right size */}
      {isSlideElementText(localElement) && !editingTextElementId && (
        <div
          style={{
            width: localElement.width / (localElement.scale || 1),
            height: localElement.height / (localElement.scale || 1),
            transform: `scale(${localElement.scale || 1})`,
            transformOrigin: 'top left',
          }}
        >
          <div ref={textElementHeightRef}>
            <SlideElementTextViewer
              element={localElement}
              richTextEditorPrefix={'slide-element-core-viwer'}
              showPlaceHolder={true}
              style={{
                cursor: isHoldingSpace
                  ? 'grab'
                  : isSelected
                  ? 'cursor'
                  : localElement.lockedPosition
                  ? 'default'
                  : 'move',
                opacity: 0, // we dont want to see the text
              }}
              content={localElement.textValue}
            />
          </div>
        </div>
      )}
      {/* The edit text view that is used to edit the Slate text */}
      {isSlideElementText(localElement) &&
        !!editingTextElementId &&
        !!richTextEditor &&
        richTextEditor.id === `${RICHTEXTEDITOR_ID_PREFIX}${element.id}` &&
        richTextEditor.children.length > 0 && (
          <>
            {/* This is to ignore the hotkeys when editing text */}
            <SlideElementTextEditor
              element={localElement}
              onTextElementChange={onTextElementChange}
              index={index}
              setSelectedElements={setSelectedElements}
              richTextEditor={richTextEditor}
              content={localElement.textValue}
              autoFocus={editingTextElementId ? true : false}
            />
          </>
        )}
      {isSlideElementIframe(localElement) && canInteractWithIframe && (
        <SlideElementIframe isInteract={true} element={localElement} slideMode={'edit'} />
      )}
    </ElementContainer>
  );
};

const rotateSvg = `<svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.98639 0C2.8679 0 0.304397 2.37339 0.00263274 5.41276C-0.0271301 5.71253 0.191754 5.97966 0.491525 6.00943C0.791295 6.03919 1.05843 5.82031 1.0882 5.52054C1.33511 3.0336 3.43423 1.0909 5.98639 1.0909C7.69326 1.0909 9.19806 1.95968 10.0814 3.28051H8.72043C8.41919 3.28051 8.17498 3.52472 8.17498 3.82596C8.17498 4.12721 8.41919 4.37141 8.72043 4.37141H10.9783C10.9925 4.37197 11.0068 4.37198 11.0211 4.37141H11.1264C11.6088 4.37141 11.9999 3.9803 11.9999 3.49788V1.09197C11.9999 0.790723 11.7557 0.546516 11.4544 0.546516C11.1532 0.546516 10.909 0.790723 10.909 1.09197V2.55878C9.82132 1.01182 8.02246 0 5.98639 0ZM11.5083 6.01758C11.8081 6.04735 12.027 6.31448 11.9972 6.61425C11.6955 9.65362 9.13193 12.027 6.01345 12.027C3.9774 12.027 2.17851 11.0152 1.09087 9.46822V10.9349C1.09087 11.2362 0.84666 11.4804 0.545416 11.4804C0.244172 11.4804 -3.46792e-05 11.2362 -3.46792e-05 10.9349V8.52902C-3.46792e-05 8.04655 0.391076 7.65549 0.873496 7.65549H0.981709C0.994033 7.65507 1.00633 7.65507 1.01859 7.65549H3.27941C3.58066 7.65549 3.82486 7.8997 3.82486 8.20094C3.82486 8.50218 3.58066 8.74639 3.27941 8.74639H1.9184C2.80169 10.0673 4.30654 10.9361 6.01345 10.9361C8.56561 10.9361 10.6647 8.9934 10.9116 6.50648C10.9414 6.20671 11.2085 5.98782 11.5083 6.01758Z" fill="black"/>
</svg>
`;

const StyledMoveable = styled(Moveable)<{
  $isEditingText?: boolean;
  $isTextElement: boolean;
  $isGeneratorElement?: boolean;
  $multipleElementsSelected: boolean;
  $zoom: number;
}>`
  z-index: ${zIndexes('CREATOR_MOVABLE_HANDLES')} !important;
  .moveable-control {
    border: solid 1px ${theme.colors.grey} !important;
  }
  .moveable-rotation {
    height: calc(40px * var(--zoom)) !important;
  }

  .moveable-line.moveable-rotation-line {
    opacity: 0;
  }

  .moveable-control.moveable-rotation-control {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 1;
    transform: unset !important;
    transform: scale(${({ $zoom }) => 1 / $zoom}) !important;

  }
  .moveable-control.moveable-rotation-control::after {
    content: url('data:image/svg+xml;utf8,${rotateSvg}');
    font-size: 24px;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 12px;
    
    margin-top: -1px;
    opacity: 1;
    &&:hover {
        color: ${theme.colors.iconGrey};
      }
  } 

  ${({ $isEditingText }) => {
    if ($isEditingText) {
      return css`
        .moveable-line,
        .moveable-rotation,
        .moveable-control {
          display: none !important;
        }
      `;
    }
  }}



.moveable-line.moveable-guideline.solidVertical {
    border: none !important;
    background: ${theme.colors.orange} !important;
    height: 1000vh !important;
    top: -500vh !important;
  }
  .moveable-line.moveable-guideline.solidHorizontal {
    border: none !important;
    background: ${theme.colors.orange} !important;
    width: 1000vw !important;
    left: -500vw !important; 
  }
 
  .moveable-line.moveable-guideline.dashedVertical {
    border: 1px dashed ${theme.colors.orange} !important;
    background: transparent !important;
    height: 1000vh !important;
    transform: scaleY(5) !important; // to stretch out the dashed lines
    top: -500vh !important;
  }
  .moveable-line.moveable-guideline.dashedHorizontal {
    border: 1px dashed ${theme.colors.orange} !important;
    background: transparent !important;
    width: 1000vw !important;
    left: -500vw !important; 
    transform: scaleX(5) !important; // to stretch out the dashed lines
  }

 

  .moveable-line.moveable-horizontal.moveable-bold {
    border: none;
    background: none !important;
    height: 1px !important;
    border-top: dashed 1px ${theme.colors.orange} !important;
  }
  .moveable-line.moveable-vertical.moveable-bold {
    border: none;
    background: none !important;
    height: 1px !important;
    border-left: dashed 1px ${theme.colors.orange} !important;
  }

  ${({ $isGeneratorElement }) =>
    $isGeneratorElement &&
    css`
      .moveable-line.moveable-direction,
      .moveable-line.moveable-rotation-line {
        background: ${theme.colors.purple} !important;
      }
    `}
`;

const ElementContainer = styled.div<{
  $isTextElement: boolean;
  $isSelected: boolean;
  $anElementIsTransforming: boolean;
  $animateBorder?: boolean;
  $isGeneratorElement?: boolean;
}>`
  position: absolute;
  transform-origin: center;
  border-width: 2px;
  cursor: pointer;
  // You are hovering an element with yout cursor while not doing anything else
  ${({ $isSelected, $anElementIsTransforming, $isGeneratorElement }) =>
    !$isSelected &&
    !$anElementIsTransforming &&
    css`
      &&:hover {
        outline: ${1}px solid
          ${$isGeneratorElement ? theme.colors.purple : theme.colors.orange} !important;
      }
    `};
`;

const LockedPositionOverlay = styled.div<{
  $disabled: boolean;
  $x: number;
  $y: number;
  $zoom: number;
}>`
  background-color: white;
  background-color: ${({ $disabled }) => $disabled && theme.colors.greyLight};

  position: absolute;
  top: ${({ $y, $zoom }) => $y - 30 * $zoom}px;
  left: ${({ $x, $zoom }) => $x - 30 * $zoom}px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  border-radius: 5px;
  transform: ${({ $zoom }) => `scale(${1 / $zoom}) translate(-0%,-50%)`};
  height: 30px;
  width: 30px;

  opacity: ${({ $disabled }) => $disabled && 0.8};
  z-index: ${zIndexes('CREATOR_LOCKED_POSITION_OVERLAY')};
  box-shadow: 0px 0px 5px ${theme.colors.grey};
`;
const DuplicateAndDeleteOverlay = styled.div<{
  $disabled: boolean;
  $x: number;
  $y: number;
  $includesWandTool: boolean;
  $zoom: number;
}>`
  background-color: white;
  background-color: ${({ $disabled }) => $disabled && theme.colors.greyLight};

  position: absolute;
  top: ${({ $y, $zoom, $includesWandTool }) =>
    $y - ($includesWandTool ? 80 : 55) * $zoom}px;
  left: ${({ $x, $zoom, $includesWandTool }) =>
    $x - ($includesWandTool ? 80 : 55) * $zoom}px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 10px;
  border-radius: 5px;
  height: 30px;
  transform: ${({ $zoom }) => `scale(${1 / $zoom}) translate(-0%,-75%)`};
  width: ${({ $includesWandTool }) => ($includesWandTool ? 80 : 55)}px;

  opacity: ${({ $disabled }) => $disabled && 0.8};
  z-index: ${zIndexes('CREATOR_LOCKED_POSITION_OVERLAY')};
  box-shadow: 0px 0px 5px ${theme.colors.grey};

  div:hover {
    color: ${theme.colors.iconGrey};
    transition: color 0.2s;
  }
`;

const IconOverLay = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  height: 100%;
  width: 100%;
  background-color: transparent;
`;
export default ProxySlideEditorSurface;
